// src/features/competicao/competicaoApi.js
//
// Cliente do backend FastAPI para a verificação de competição (REGRAS_V2).
// Todas as rotas exigem o Firebase ID token (mesmo padrão do chat do VITA).
// O CLIENT NUNCA DECIDE: quem valida ticket/duração/tipo é o backend — estas
// funções só transportam e devolvem o veredito para a UI avisar o usuário.

import { getAuth } from "firebase/auth";
import { apiUrl } from "../../config/api";

async function authHeaders() {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Usuário não autenticado.");
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function post(path, body) {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && !data?.ok) {
    throw new Error(data?.error || data?.erro || `Falha na chamada ${path}`);
  }
  return data;
}

async function get(path) {
  const res = await fetch(apiUrl(path), { headers: await authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Falha na chamada ${path}`);
  return data;
}

/**
 * Pré-checagem ANTES de registrar (não escreve nada): o app usa a resposta
 * para o aviso "vai gerar XP mas não pontua porque …".
 * Quando o registro JÁ EXISTE, passe `origemId`: o backend responde o destino
 * REAL daquele registro em vez de simular o PRÓXIMO consumo de ticket. É o que
 * evita o falso "não vai pontuar" na refeição — o scan já criou o evento (e já
 * pagou o ticket) antes desta tela abrir.
 *
 * @param {"TREINO"|"REFEICAO"} tipo
 * @param {{tipoAtividadeId?: string, duracaoMin?: number, origemId?: string}} [opts]
 */
export function prechecarCompeticao(tipo, opts = {}) {
  return post("/competicao/prechecar", {
    tipo,
    tipo_atividade_id: opts.tipoAtividadeId || null,
    duracao_min: Number(opts.duracaoMin) || 0,
    origem_id: opts.origemId || null,
  });
}

/**
 * Cria o evento de competição APÓS o registro normal (XP) ter sido salvo.
 * Consome ticket e entra PENDENTE na fila de verificação do dono.
 */
export function criarEventoCompeticao({ tipo, origemId, tipoAtividadeId, duracaoMin, isLive, fotoPath }) {
  return post("/competicao/eventos", {
    tipo,
    origem_id: origemId,
    tipo_atividade_id: tipoAtividadeId || null,
    duracao_min: Number(duracaoMin) || 0,
    is_live: isLive === true,
    foto_path: fotoPath || null,
  });
}

/** Saldo de tickets (treino, refeição, análise corporal) para a tela de tickets. */
export function getTicketsSaldo() {
  return get("/tickets/saldo");
}

/**
 * Pontos LÍQUIDOS de um registro na competição (v3.7.3 — Bloco G.3): soma dos
 * eventos do registro (original + versões de edição) que ainda contam.
 * @param {"TREINO"|"REFEICAO"} tipo
 * @param {string} origemId id do registro de origem
 * @return {Promise<{ok: boolean, pontos?: number, status?: string}>}
 */
export function getPontosRegistro(tipo, origemId) {
  return get(`/competicao/pontos-registro?tipo=${encodeURIComponent(tipo)}` +
    `&origem_id=${encodeURIComponent(origemId)}`);
}

// Motivos que a UI traduz em aviso amigável (espelho do backend).
export const MOTIVOS = {
  REGRAS_V2_DESLIGADA: null, // silencioso: feature desligada
  SEM_COMPETICAO: null,      // silencioso: academia sem competição v2
  TIPO_NAO_PONTUA:
    "este tipo de atividade não pontua na competição (só exercício físico).",
  DURACAO_MINIMA:
    "a duração ficou abaixo de 40 minutos.",
  SEM_FOTO:
    "é obrigatório tirar uma foto pela câmera para pontuar.",
  SEM_TICKETS:
    "seus tickets de hoje acabaram.",
  TETO_DIARIO:
    "você já usou o máximo de registros pontuáveis de hoje.",
};

/** Mensagem do aviso pré-registro; null = não avisar (segue silencioso). */
export function mensagemDoMotivo(motivo) {
  const m = MOTIVOS[motivo];
  if (!m) return null;
  return `Este registro vai gerar XP, mas NÃO vai pontuar na competição porque ${m}`;
}
